import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import type {
  TrafficTimeseries,
  TrafficSources,
  TopPages,
  GeographyData,
  DeviceBreakdown,
  UserTypes,
} from '../../types/analytics';

const CHART_COLORS = [
  BRAND_COLORS.forestGreen.main,
  BRAND_COLORS.yellow.main,
  '#2196F3',
  '#FF5722',
  '#9C27B0',
  '#00BCD4',
  '#FF9800',
  '#607D8B',
];

// Custom pie tooltip showing name, value, percentage with color dot
const PieTooltipContent: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { percentage?: number } }>;
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: data } = payload[0];
  const pct = data.percentage ?? 0;
  return (
    <Paper sx={{ px: 1.5, py: 1, fontSize: 12 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {value.toLocaleString()} sessions ({pct}%)
      </Typography>
    </Paper>
  );
};

const ChartCard: React.FC<{
  title: string;
  loading: boolean;
  height?: number;
  children: React.ReactNode;
}> = ({ title, loading, height = 300, children }) => (
  <Paper sx={{ p: 2.5 }}>
    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
      {title}
    </Typography>
    {loading ? (
      <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />
    ) : (
      <Box sx={{ width: '100%', height }}>{children}</Box>
    )}
  </Paper>
);

interface AnalyticsChartsProps {
  traffic: TrafficTimeseries | null;
  sources: TrafficSources | null;
  pages: TopPages | null;
  geography: GeographyData | null;
  devices: DeviceBreakdown | null;
  userTypes: UserTypes | null;
  trafficLoading: boolean;
  sourcesLoading: boolean;
  pagesLoading: boolean;
  geographyLoading: boolean;
  devicesLoading: boolean;
  userTypesLoading: boolean;
}

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
  traffic,
  sources,
  pages,
  geography,
  devices,
  userTypes,
  trafficLoading,
  sourcesLoading,
  pagesLoading,
  geographyLoading,
  devicesLoading,
  userTypesLoading,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: 3,
      mb: 3,
    }}
  >
    {/* Traffic Over Time */}
    <ChartCard title="Traffic Over Time" loading={trafficLoading} height={280}>
      {traffic && traffic.data.length > 0 ? (
        <ResponsiveContainer>
          <LineChart data={traffic.data}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={alpha(BRAND_COLORS.forestGreen.main, 0.1)}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickFormatter={(v: string) => {
                const d = new Date(v);
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <RechartsTooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="active_users"
              name="Users"
              stroke={BRAND_COLORS.forestGreen.main}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="sessions"
              name="Sessions"
              stroke={BRAND_COLORS.yellow.main}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="page_views"
              name="Page Views"
              stroke="#2196F3"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No traffic data available
        </Typography>
      )}
    </ChartCard>

    {/* Traffic Sources */}
    <ChartCard title="Traffic Sources" loading={sourcesLoading} height={280}>
      {sources && sources.data.length > 0 ? (
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={sources.data}
              dataKey="sessions"
              nameKey="source"
              cx="40%"
              cy="50%"
              outerRadius={90}
              innerRadius={45}
            >
              {sources.data.map((_, idx) => (
                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip content={<PieTooltipContent />} />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, lineHeight: '18px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No source data available
        </Typography>
      )}
    </ChartCard>

    {/* Top Pages */}
    <ChartCard title="Top Pages" loading={pagesLoading} height={280}>
      {pages && pages.data.length > 0 ? (
        <TableContainer sx={{ maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Views
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Avg. Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.data.map((page) => (
                <TableRow key={page.page_path}>
                  <TableCell
                    sx={{
                      maxWidth: 250,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip title={page.page_title || page.page_path}>
                      <span>{page.page_path}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{page.page_views.toLocaleString()}</TableCell>
                  <TableCell align="right">{page.avg_engagement_time.toFixed(0)}s</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No page data available
        </Typography>
      )}
    </ChartCard>

    {/* Geography */}
    <ChartCard title="Top Countries" loading={geographyLoading} height={280}>
      {geography && geography.data.length > 0 ? (
        <ResponsiveContainer>
          <BarChart data={geography.data} layout="vertical">
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={alpha(BRAND_COLORS.forestGreen.main, 0.1)}
            />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} width={100} />
            <RechartsTooltip />
            <Bar
              dataKey="sessions"
              name="Sessions"
              fill={BRAND_COLORS.forestGreen.main}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No geography data available
        </Typography>
      )}
    </ChartCard>

    {/* Devices */}
    <ChartCard title="Device Breakdown" loading={devicesLoading} height={280}>
      {devices && devices.data.length > 0 ? (
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={devices.data}
              dataKey="sessions"
              nameKey="device_category"
              cx="40%"
              cy="50%"
              outerRadius={90}
              innerRadius={45}
            >
              {devices.data.map((_, idx) => (
                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip content={<PieTooltipContent />} />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, lineHeight: '18px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No device data available
        </Typography>
      )}
    </ChartCard>

    {/* New vs Returning */}
    <ChartCard title="New vs Returning Users" loading={userTypesLoading} height={280}>
      {userTypes ? (
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={[
                { name: 'New Users', value: userTypes.new_users },
                { name: 'Returning Users', value: userTypes.returning_users },
              ]}
              dataKey="value"
              nameKey="name"
              cx="40%"
              cy="50%"
              outerRadius={90}
              innerRadius={45}
            >
              <Cell fill={BRAND_COLORS.forestGreen.main} />
              <Cell fill={BRAND_COLORS.yellow.main} />
            </Pie>
            <RechartsTooltip />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, lineHeight: '18px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
          No user data available
        </Typography>
      )}
    </ChartCard>
  </Box>
);

export default AnalyticsCharts;
